import {
  type SearchResult,
  isCloneType,
  isDemoType,
  isOverlayType,
  isPlaybookType,
  isTourType,
} from '@carboncopyco/data';
import {
  DemoIndicatorColoredIcon,
  FolderIcon,
  PlaybookIcon,
  RecordingIndicatorColoredIcon,
  SharedFolderIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import { RecordingTypes, type WorkspaceFolder } from '@carboncopyco/types';
import {
  ColorHex,
  LoadingDots,
  SkeletonRowLoading,
  TextBase,
  TextButton,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import {
  MMMDoYYYY,
  dateUTCFormat,
  integerWithCommas,
} from '@carboncopyco/utils';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { type Col, FixedTable } from '../../FixedTable';
import type { ExcludedEntities } from '../../types';
import { WorkspacePageTableRowWrapper } from './WorkspacePageTableRowWrapper';

interface WorkspacePageTableViewProps {
  items: SearchResult[];
  folders: WorkspaceFolder[];
  isLoading: boolean;
  isLoadingBatch: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  fetchNextBatch: () => void;
}

export type ExtendedWorkspaceFolder = WorkspaceFolder & {
  entityType: 'folder';
  readOnly?: boolean;
};

export const isFolderType = (
  item: Record<string, unknown>
): item is ExtendedWorkspaceFolder => item.entityType === 'folder';

const iconByEntityType = {
  record: <RecordingIndicatorColoredIcon size={18} />,
  demo: <DemoIndicatorColoredIcon size={18} />,
  tour: <TourIndicatorColoredIcon size={18} />,
  overlay: <DemoIndicatorColoredIcon size={18} />,
  playbook: <PlaybookIcon size={18} />,
  folder: (readOnly?: boolean) =>
    readOnly ? (
      <SharedFolderIcon
        size={20}
        color={ColorHex.natural500}
      />
    ) : (
      <FolderIcon
        size={20}
        color={ColorHex.natural500}
      />
    ),
};
const showIconByEntity = (
  item: SearchResult | ExtendedWorkspaceFolder
): ReactElement => {
  return item.entityType === 'folder'
    ? iconByEntityType[item.entityType](item?.readOnly)
    : iconByEntityType[item.entityType];
};

const showTooltipByEntity: Record<ExcludedEntities, string> = {
  record: 'Clone',
  demo: 'Demo (cloned app)',
  tour: 'Tour',
  overlay: 'Demo (live app)',
  playbook: 'Playbooks contain a variety of assets and web resources',
};

const getAssetNameByEntity = (item: SearchResult | ExtendedWorkspaceFolder) => {
  if (isCloneType(item)) {
    return item.recordName;
  }
  if (isDemoType(item) || isOverlayType(item)) {
    return item.demoName;
  }
  if (isTourType(item)) {
    return item.tourName;
  }
  if (isPlaybookType(item) || isFolderType(item)) {
    return item.name;
  }
  return 'Missing title';
};

const cols: Col<SearchResult | ExtendedWorkspaceFolder>[] = [
  {
    title: 'Type',
    width: 45,
    render: (item) => {
      return (
        <TooltipBubble
          title={showTooltipByEntity[item.entityType]}
          placement="top"
          disabled={item.entityType === 'folder'}
        >
          <div className="flex flex-col">{showIconByEntity(item)}</div>
        </TooltipBubble>
      );
    },
  },
  {
    title: 'Name',
    width: 290,
    maxContentWidth: 270,
    render: (item) => {
      return (
        <TooltipBubble
          title={getAssetNameByEntity(item)}
          placement="top-start"
        >
          <div className="flex items-center gap-1">
            <div className="truncate">
              <TextButton
                ellipsis
                label={getAssetNameByEntity(item)}
                color="text-ds-natural-700"
              />
            </div>
            {isFolderType(item) && (
              <TextButton
                label={`(${item?.totalItems ?? 0})`}
                color="text-ds-natural-300"
              />
            )}
          </div>
        </TooltipBubble>
      );
    },
  },
  {
    title: 'Created by',
    width: 200,
    render: (item) => (
      <UserBasicInfo
        user={item.creator}
        truncateWidth={140}
        textColor="text-ds-natural-600"
        tooltip={item?.creator?.name?.length > 13}
        size="small"
      />
    ),
  },
  {
    title: 'Last edited',
    width: 168,
    maxContentWidth: 340,
    render: (item) => (
      <TooltipBubble
        title={`Edited on ${dateUTCFormat(item.updateTime)}`}
        placement="top"
      >
        <TextBase
          color="text-ds-natural-300"
          label={MMMDoYYYY(item.updateTime)}
        />
      </TooltipBubble>
    ),
  },
  {
    title: 'Creation source',
    width: 120,
    render: (item) => {
      const isNativeRecording =
        (isCloneType(item) || isDemoType(item)) &&
        item.recordingType === RecordingTypes.native;
      const isTourFromDesktop = isTourType(item) && item.source === 'desktop';
      const label =
        isNativeRecording || isTourFromDesktop ? 'Desktop app' : 'Website';
      return (
        <TextBase
          label={label}
          color="text-ds-natural-600"
        />
      );
    },
  },
  {
    title: 'Presentations',
    width: 100,
    alignment: 'right',
    render: (item) => (
      <TextBase
        label={
          (isDemoType(item) || isOverlayType(item)) &&
          item.totalPresentations &&
          item.totalPresentations > 0
            ? integerWithCommas(item.totalPresentations)
            : ''
        }
        color="text-ds-natural-600"
      />
    ),
  },
  {
    title: 'Shares',
    width: 70,
    alignment: 'right',
    render: (item) => (
      <TextBase
        label={
          (isDemoType(item) || isTourType(item)) &&
          item.totalShares &&
          item.totalShares > 0
            ? integerWithCommas(item.totalShares)
            : ''
        }
        color="text-ds-natural-600"
      />
    ),
  },
];

export const WorkspacePageTableView = ({
  items,
  folders,
  isLoading,
  isLoadingBatch,
  error,
  fetchNextBatch,
}: WorkspacePageTableViewProps) => {
  const foldersWithEntityType: ExtendedWorkspaceFolder[] =
    folders?.map((folder) => {
      return {
        ...folder,
        entityType: 'folder',
      };
    }) || [];

  const itemsToRender = useMemo<(ExtendedWorkspaceFolder | SearchResult)[]>(
    () => [...foldersWithEntityType, ...items],
    [foldersWithEntityType, items]
  );

  const tableParentRef = useRef<HTMLDivElement>();

  const [listBottomRef, _, entry] = useInView({
    root: tableParentRef.current,
  });

  useEffect(() => {
    if (itemsToRender?.length && entry?.isIntersecting) {
      fetchNextBatch();
    }
  }, [entry, fetchNextBatch, itemsToRender]);

  return (
    <div
      ref={tableParentRef}
      className="overflow-y-auto relative w-full"
    >
      <FixedTable<SearchResult | ExtendedWorkspaceFolder>
        headerRowClassName="pt-4 pb-1 sticky top-0 left-0 right-0 bg-ds-accent3-100 z-[5]"
        rowWrapper={WorkspacePageTableRowWrapper}
        cols={cols}
        data={itemsToRender}
        isLoading={isLoading}
        error={error}
        loader={<SkeletonRowLoading itemsCount={10} />}
        noWrapperDiv
      />
      {isLoadingBatch && (
        <div className="flex justify-center">
          <LoadingDots />
        </div>
      )}
      <div ref={listBottomRef} />
    </div>
  );
};
